import { FieldOption, type FilterInput } from '../constants';
import { QUICK_SEARCH_MIN_LENGTH } from './quickSearchConfig';
import {
  hasWildcardChars,
  normalizeWildcardInput,
} from './animalIdentChipFilter';
import { getQuickSearchDefaultOperator } from './quickSearchOperator';

/** Maps legacy per-column hero fields to composite search fields. */
function normalizeHeroSearchField(field: string): string {
  if (
    field === FieldOption.memberIdent.value ||
    field === FieldOption.memberName.value
  ) {
    return FieldOption.member.value;
  }
  return field;
}

/**
 * Builds a single quick-search filter row from a hero/basic-dialog term.
 * Returns null when the term is empty or below min length (caller should preserve filters).
 */
export function buildQuickSearchFilterFromTerm(
  searchTerm: string,
  field: string,
  defaultOperator: string = getQuickSearchDefaultOperator(),
): FilterInput | null {
  const trimmed = searchTerm.trim();
  if (!trimmed || trimmed.length < QUICK_SEARCH_MIN_LENGTH) {
    return null;
  }

  const gqlField = normalizeHeroSearchField(field);
  const normalized = normalizeWildcardInput(trimmed);

  // Comma-separated idents/names and `%`/`*` patterns use chip-style rows
  // (empty operator) so buildAnimalFilterGroups expands them as OR equals /
  // wildcard chips — not a single STARTS_WITH on the whole string.
  if (hasWildcardChars(normalized) || normalized.includes(',')) {
    return { field: gqlField, operator: '', value: normalized };
  }

  return {
    field: gqlField,
    operator: defaultOperator,
    value: trimmed,
  };
}

const LEGACY_HERO_FIELD_ALIASES = new Set([
  FieldOption.animalName.value,
  FieldOption.animalIdent.value,
  FieldOption.memberIdent.value,
  FieldOption.memberName.value,
]);

/**
 * Upserts a hero/basic-dialog search term into a filter list, replacing legacy
 * per-column rows that the composite field supersedes.
 */
export function upsertHeroSearchTermInFilters(
  filters: FilterInput[],
  searchTerm: string,
  compositeField: string,
): FilterInput[] {
  const trimmed = searchTerm.trim();
  const withoutLegacy = filters.filter(
    (f) =>
      f.field !== compositeField && !LEGACY_HERO_FIELD_ALIASES.has(f.field),
  );

  if (!trimmed) {
    return withoutLegacy;
  }

  const built = buildQuickSearchFilterFromTerm(trimmed, compositeField);
  if (!built) {
    return filters;
  }

  const existingIndex = withoutLegacy.findIndex((f) => f.field === built.field);
  if (existingIndex >= 0) {
    const next = [...withoutLegacy];
    next[existingIndex] = built;
    return next;
  }

  return [...withoutLegacy, built];
}
